/**
 * Inventory Adjustment Page - ISS-FE-008
 * AS9100D-Compliant Inventory Adjustment Management
 * 
 * Features:
 * - Create adjustments (Increase, Decrease, Write-off, Found, Correction)
 * - Lot and location selection
 * - Reason codes per AS9100D 10.2
 * - E-signature for approvals
 * - Pending approval queue
 * - Adjustment history
 * 
 * AS9100D Clauses:
 * - 10.2: Nonconformity and Corrective Action
 * - 8.5.2: Identification and Traceability
 * - 7.5: Documented Information
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Plus,
  FileText,
  Shield,
  Package,
  MapPin,
  RefreshCw,
  Loader2,
  ChevronRight,
  Filter,
  Eye,
  Check,
  X,
  History,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Separator } from '../components/ui/separator';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import ComplianceTooltip from '../components/ComplianceTooltip';
import { ESignatureDialog, ESignatureBadge } from '../components/ESignatureDialog';
import InventoryLayout from '../components/layouts/InventoryLayout';

// Adjustment type configurations
const ADJUSTMENT_TYPES = [
  { value: 'INCREASE', label: 'Increase', icon: ArrowUpCircle, color: 'text-green-600', description: 'Add inventory (receipt adjustment, found items)' },
  { value: 'DECREASE', label: 'Decrease', icon: ArrowDownCircle, color: 'text-red-600', description: 'Remove inventory (damage, loss, scrap)' },
  { value: 'WRITE_OFF', label: 'Write Off', icon: Trash2, color: 'text-red-700', description: 'Complete removal (expired, obsolete)' },
  { value: 'FOUND', label: 'Found', icon: Search, color: 'text-blue-600', description: 'Previously unrecorded inventory' },
  { value: 'CORRECTION', label: 'Correction', icon: FileText, color: 'text-amber-600', description: 'Error correction (+/-)' }
];

// Status configurations
const STATUS_CONFIG = {
  'PENDING_APPROVAL': { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  'APPROVED': { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  'REJECTED': { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  'APPLIED': { label: 'Applied', color: 'bg-blue-100 text-blue-700', icon: Check }
};

/**
 * Create Adjustment Dialog
 */
const CreateAdjustmentDialog = ({ open, onOpenChange, onSuccess, lots, locations, reasonCodes }) => {
  const [step, setStep] = useState(1);
  const [lotSearch, setLotSearch] = useState('');
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [explanation, setExplanation] = useState('');
  const [showESignature, setShowESignature] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lotBalances, setLotBalances] = useState([]);
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setLotSearch('');
      setSelectedLot(null);
      setSelectedLocation(null);
      setAdjustmentType('');
      setQuantity('');
      setReasonCode('');
      setExplanation('');
      setLotBalances([]);
    }
  }, [open]);
  
  // Load lot balances when lot is selected
  useEffect(() => {
    if (selectedLot) {
      loadLotBalances(selectedLot.lot_id, selectedLot);
    }
  }, [selectedLot]);
  
  const loadLotBalances = async (lotId, lot) => {
    try {
      const response = await api.get(`/api/v1/inventory/v2/lots/${lotId}/balances`);
      const balances = response.data?.balances || [];
      
      // If no balances exist but lot has quantity, create a virtual balance
      if (balances.length === 0 && lot) {
        const virtualBalance = {
          balance_id: `virtual_${lotId}`,
          lot_id: lotId,
          location_id: lot.location_id || 'DEFAULT',
          location_code: lot.location_id || 'Primary Location',
          quantity: lot.current_quantity || 0
        };
        setLotBalances([virtualBalance]);
        // Auto-select the only location
        setSelectedLocation(virtualBalance);
      } else {
        setLotBalances(balances);
        // Auto-select if only one location
        if (balances.length === 1) {
          setSelectedLocation(balances[0]);
        }
      }
    } catch (err) {
      console.error('Error loading lot balances:', err);
      // On error, create virtual balance
      if (lot) {
        const virtualBalance = {
          balance_id: `virtual_${lotId}`,
          lot_id: lotId,
          location_id: lot.location_id || 'DEFAULT',
          location_code: lot.location_id || 'Primary Location',
          quantity: lot.current_quantity || 0
        };
        setLotBalances([virtualBalance]);
        setSelectedLocation(virtualBalance);
      } else {
        setLotBalances([]);
      }
    }
  };
  
  // Filter lots based on search
  const filteredLots = lots.filter(lot => 
    lot.lot_number?.toLowerCase().includes(lotSearch.toLowerCase()) ||
    lot.part_number?.toLowerCase().includes(lotSearch.toLowerCase())
  ).slice(0, 10);
  
  // Calculate if approval is needed
  const needsApproval = () => {
    if (!selectedLot || !quantity) return false;
    const currentQty = selectedLot.current_quantity || 0;
    const adjustQty = parseFloat(quantity) || 0;
    const pct = currentQty > 0 ? (adjustQty / currentQty * 100) : 100;
    return pct >= 1;
  };
  
  const getApprovalLevel = () => {
    if (!selectedLot || !quantity) return null;
    const currentQty = selectedLot.current_quantity || 0;
    const adjustQty = parseFloat(quantity) || 0;
    const pct = currentQty > 0 ? (adjustQty / currentQty * 100) : 100;
    
    if (pct >= 10) return 'MANAGER';
    if (pct >= 5) return 'MANAGER';
    if (pct >= 1) return 'SUPERVISOR';
    return null;
  };
  
  const handleSubmit = async (signature = null) => {
    if (!selectedLot || !selectedLocation || !adjustmentType || !quantity || !reasonCode) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (needsApproval() && !signature) {
      setShowESignature(true);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const payload = {
        lot_id: selectedLot.lot_id,
        location_id: selectedLocation.location_id || selectedLocation.balance_id,
        adjustment_type: adjustmentType,
        quantity: parseFloat(quantity),
        reason_code: reasonCode,
        explanation: explanation,
        e_signature: signature ? {
          signed_by: signature.user_name,
          signed_by_id: signature.user_id,
          signed_at: signature.timestamp,
          acknowledged: signature.acknowledged
        } : null
      };
      
      await api.post('/api/v1/inventory/v2/adjustments', payload);
      toast.success('Adjustment created successfully');
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Error creating adjustment:', err);
      toast.error(err.response?.data?.detail || 'Failed to create adjustment');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSignatureConfirm = (signatureData) => {
    setShowESignature(false);
    handleSubmit(signatureData);
  };
  
  // Filter reason codes based on adjustment type
  const filteredReasonCodes = reasonCodes.filter(rc => {
    if (adjustmentType === 'INCREASE') return ['FOUND', 'MISCOUNT', 'DATA_ERROR', 'RECEIPT_ADJ', 'OTHER'].includes(rc.code);
    if (adjustmentType === 'DECREASE') return ['DAMAGE', 'SCRAP', 'THEFT_LOSS', 'REWORK', 'SAMPLE', 'OTHER'].includes(rc.code);
    if (adjustmentType === 'WRITE_OFF') return ['EXPIRY', 'OBSOLETE', 'SCRAP', 'OTHER'].includes(rc.code);
    if (adjustmentType === 'FOUND') return ['FOUND', 'MISCOUNT', 'OTHER'].includes(rc.code);
    if (adjustmentType === 'CORRECTION') return true;
    return true;
  });
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#101957]" />
              Create Inventory Adjustment
              <ComplianceTooltip tooltipId="adjustment.create" />
            </DialogTitle>
            <DialogDescription>
              AS9100D 10.2: Document the adjustment with reason and approval
            </DialogDescription>
          </DialogHeader>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-[#101957] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s}
                </div>
                <span className={`ml-2 text-sm ${step >= s ? 'text-[#101957] font-medium' : 'text-gray-400'}`}>
                  {s === 1 ? 'Select Lot' : s === 2 ? 'Adjustment Details' : 'Confirm'}
                </span>
                {s < 3 && <ChevronRight className="w-4 h-4 mx-4 text-gray-300" />}
              </div>
            ))}
          </div>
          
          {/* Step 1: Select Lot */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by lot number or part number..."
                  value={lotSearch}
                  onChange={(e) => setLotSearch(e.target.value)}
                  className="pl-10"
                  data-testid="lot-search-input"
                />
              </div>
              
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {filteredLots.length > 0 ? (
                  filteredLots.map(lot => (
                    <div
                      key={lot.lot_id}
                      className={`flex items-center justify-between p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 ${
                        selectedLot?.lot_id === lot.lot_id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                      onClick={() => setSelectedLot(lot)}
                      data-testid={`lot-option-${lot.lot_number}`}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{lot.lot_number}</p>
                        <p className="text-sm text-gray-500">{lot.part_number} - {lot.part_description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-medium">{lot.current_quantity} {lot.uom}</p>
                        <Badge variant="outline" className="text-xs">{lot.status}</Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    {lotSearch ? 'No lots found matching your search' : 'Start typing to search for lots'}
                  </div>
                )}
              </div>
              
              {/* Selected Lot Summary */}
              {selectedLot && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Selected Lot</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Lot Number:</span>
                      <p className="font-medium">{selectedLot.lot_number}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Part Number:</span>
                      <p className="font-medium">{selectedLot.part_number}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Current Qty:</span>
                      <p className="font-mono font-medium">{selectedLot.current_quantity} {selectedLot.uom}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p className="font-medium">{selectedLot.status}</p>
                    </div>
                  </div>
                  
                  {/* Location Selection */}
                  {lotBalances.length > 0 && (
                    <div className="mt-4">
                      <Label>Select Location <span className="text-red-500">*</span></Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {lotBalances.map(bal => (
                          <div
                            key={bal.balance_id}
                            className={`p-2 border rounded cursor-pointer ${
                              selectedLocation?.balance_id === bal.balance_id 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                            onClick={() => setSelectedLocation(bal)}
                          >
                            <p className="text-sm font-medium">{bal.location_code || bal.location_id}</p>
                            <p className="text-xs text-gray-500">{bal.quantity} {selectedLot.uom}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Step 2: Adjustment Details */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Adjustment Type */}
              <div className="space-y-2">
                <Label>Adjustment Type <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-5 gap-2">
                  {ADJUSTMENT_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <TooltipProvider key={type.value}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant={adjustmentType === type.value ? 'default' : 'outline'}
                              className={`flex-col h-auto py-3 ${
                                adjustmentType === type.value ? '' : type.color
                              }`}
                              onClick={() => {
                                setAdjustmentType(type.value);
                                setReasonCode(''); // Reset reason when type changes
                              }}
                              data-testid={`adj-type-${type.value.toLowerCase()}`}
                            >
                              <Icon className="w-5 h-5 mb-1" />
                              <span className="text-xs">{type.label}</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">{type.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
              </div>
              
              {/* Quantity */}
              <div className="space-y-2">
                <Label>Quantity to Adjust <span className="text-red-500">*</span></Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="0.00"
                    className="font-mono"
                    min="0.01"
                    step="0.01"
                    data-testid="quantity-input"
                  />
                  <span className="text-sm text-gray-500">{selectedLot?.uom || 'EA'}</span>
                </div>
                {selectedLot && quantity && (
                  <p className="text-xs text-gray-500">
                    Current: {selectedLot.current_quantity} → New: {
                      adjustmentType === 'DECREASE' || adjustmentType === 'WRITE_OFF'
                        ? (selectedLot.current_quantity - parseFloat(quantity || 0)).toFixed(2)
                        : (selectedLot.current_quantity + parseFloat(quantity || 0)).toFixed(2)
                    } {selectedLot.uom}
                  </p>
                )}
              </div>
              
              {/* Reason Code */}
              <div className="space-y-2">
                <Label>Reason Code <span className="text-red-500">*</span></Label>
                <Select value={reasonCode} onValueChange={setReasonCode}>
                  <SelectTrigger data-testid="reason-code-select">
                    <SelectValue placeholder="Select reason for adjustment" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredReasonCodes.map(rc => (
                      <SelectItem key={rc.code} value={rc.code}>
                        {rc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Explanation */}
              <div className="space-y-2">
                <Label>
                  Explanation <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  placeholder="Provide detailed explanation for the adjustment..."
                  rows={3}
                  data-testid="explanation-input"
                />
                <p className="text-xs text-gray-500">
                  Per AS9100D 10.2, detailed documentation is required for all adjustments.
                </p>
              </div>
              
              {/* Approval Warning */}
              {needsApproval() && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Shield className="w-4 h-4" />
                    <span className="font-medium">
                      {getApprovalLevel()} Approval Required
                    </span>
                  </div>
                  <p className="text-sm text-amber-600 mt-1">
                    This adjustment requires e-signature approval per AS9100D 10.2.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-slate-50 border rounded-lg p-4">
                <h4 className="font-semibold text-[#101957] mb-4">Adjustment Summary</h4>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Lot:</span>
                    <p className="font-medium">{selectedLot?.lot_number}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Part:</span>
                    <p className="font-medium">{selectedLot?.part_number}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Location:</span>
                    <p className="font-medium">{selectedLocation?.location_code || selectedLocation?.location_id}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <p className="font-medium">{ADJUSTMENT_TYPES.find(t => t.value === adjustmentType)?.label}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Quantity:</span>
                    <p className={`font-mono font-medium ${
                      adjustmentType === 'DECREASE' || adjustmentType === 'WRITE_OFF'
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}>
                      {adjustmentType === 'DECREASE' || adjustmentType === 'WRITE_OFF' ? '-' : '+'}
                      {quantity} {selectedLot?.uom}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Reason:</span>
                    <p className="font-medium">{reasonCodes.find(r => r.code === reasonCode)?.label}</p>
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                <div>
                  <span className="text-gray-500">Explanation:</span>
                  <p className="mt-1 text-sm">{explanation}</p>
                </div>
                
                {needsApproval() && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      This adjustment requires {getApprovalLevel()} approval with e-signature.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-6">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {step < 3 ? (
              <Button 
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 1 && (!selectedLot || !selectedLocation)) ||
                  (step === 2 && (!adjustmentType || !quantity || !reasonCode || !explanation))
                }
                data-testid="next-step-btn"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                data-testid="submit-adjustment-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : needsApproval() ? (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Sign & Submit
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Submit Adjustment
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* E-Signature Dialog */}
      <ESignatureDialog
        open={showESignature}
        onOpenChange={setShowESignature}
        onConfirm={handleSignatureConfirm}
        title="Adjustment Approval"
        description="This adjustment requires your electronic signature for AS9100D 10.2 compliance."
        actionDescription={`${adjustmentType} adjustment of ${quantity} ${selectedLot?.uom || 'EA'} for ${selectedLot?.lot_number || 'N/A'}`}
        approvalLevel={getApprovalLevel()}
        submitLabel="Sign & Approve Adjustment"
      />
    </>
  );
};

/**
 * Adjustment Row Component
 */
const AdjustmentRow = ({ adjustment, onView, onApprove, onReject }) => {
  const status = STATUS_CONFIG[adjustment.status] || STATUS_CONFIG['PENDING_APPROVAL'];
  const StatusIcon = status.icon;
  const typeConfig = ADJUSTMENT_TYPES.find(t => t.value === adjustment.adjustment_type);
  const TypeIcon = typeConfig?.icon || FileText;
  
  return (
    <TableRow className="hover:bg-slate-50">
      <TableCell>
        <div className="flex items-center gap-2">
          <TypeIcon className={`w-4 h-4 ${typeConfig?.color || 'text-gray-500'}`} />
          <div>
            <p className="font-medium">{adjustment.adjustment_number}</p>
            <p className="text-xs text-gray-500">{adjustment.adjustment_type}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <p className="font-medium">{adjustment.lot_number}</p>
        <p className="text-xs text-gray-500">{adjustment.part_number}</p>
      </TableCell>
      <TableCell className="font-mono">
        <span className={typeConfig?.value === 'DECREASE' || typeConfig?.value === 'WRITE_OFF'
          ? 'text-red-600' : 'text-green-600'}>
          {typeConfig?.value === 'DECREASE' || typeConfig?.value === 'WRITE_OFF' ? '-' : '+'}
          {adjustment.quantity} {adjustment.uom}
        </span>
      </TableCell>
      <TableCell>
        <p className="text-sm">{adjustment.reason_code}</p>
      </TableCell>
      <TableCell>
        <Badge className={status.color}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {status.label}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-gray-500">
        {adjustment.created_at ? formatIST(new Date(adjustment.created_at)) : 'N/A'}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onView(adjustment)}>
            <Eye className="w-4 h-4" />
          </Button>
          {adjustment.status === 'PENDING_APPROVAL' && (
            <>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-green-600"
                onClick={() => onApprove(adjustment)}
              >
                <Check className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-600"
                onClick={() => onReject(adjustment)}
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

/**
 * Main Adjustment Page Component
 */
const AdjustmentPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [adjustments, setAdjustments] = useState([]);
  const [lots, setLots] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [adjustmentsRes, lotsRes, reasonCodesRes] = await Promise.all([
        api.get('/api/v1/inventory/v2/adjustments', {
          params: { 
            status: activeTab === 'pending' ? 'PENDING_APPROVAL' : 
                   activeTab === 'approved' ? 'APPROVED' : null,
            page,
            page_size: 20
          }
        }),
        api.get('/api/v1/inventory/v2/lots', { params: { page_size: 100 } }),
        api.get('/api/v1/inventory/v2/adjustments/reason-codes')
      ]);
      
      setAdjustments(adjustmentsRes.data.items || []);
      setTotalPages(adjustmentsRes.data.total_pages || 1);
      setLots(lotsRes.data.items || []);
      setReasonCodes(reasonCodesRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, page]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleApprove = async (signatureData) => {
    try {
      await api.post(`/api/v1/inventory/v2/adjustments/${selectedAdjustment.adjustment_id}/approve`, {
        e_signature: {
          signed_by: signatureData.user_name,
          signed_by_id: signatureData.user_id,
          signed_at: signatureData.timestamp,
          acknowledged: signatureData.acknowledged
        }
      });
      toast.success('Adjustment approved successfully');
      setShowApproveDialog(false);
      setSelectedAdjustment(null);
      loadData();
    } catch (err) {
      console.error('Error approving adjustment:', err);
      toast.error(err.response?.data?.detail || 'Failed to approve adjustment');
    }
  };
  
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      await api.post(`/api/v1/inventory/v2/adjustments/${selectedAdjustment.adjustment_id}/reject`, {
        rejection_reason: rejectReason
      });
      toast.success('Adjustment rejected');
      setShowRejectDialog(false);
      setSelectedAdjustment(null);
      setRejectReason('');
      loadData();
    } catch (err) {
      console.error('Error rejecting adjustment:', err);
      toast.error(err.response?.data?.detail || 'Failed to reject adjustment');
    }
  };
  
  // Count pending approvals
  const pendingCount = adjustments.filter(a => a.status === 'PENDING_APPROVAL').length;
  
  return (
    <InventoryLayout>
      <div className="h-full overflow-auto" data-testid="adjustment-page">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#101957]">Inventory Adjustments</h1>
                <p className="text-sm text-gray-500">AS9100D 10.2: Nonconformity and Corrective Action</p>
              </div>
            </div>
          
          <div className="flex items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
                    <Shield className="w-3 h-3" />
                    10.2 / 7.5
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">AS9100D Compliant</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-adjustment-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Adjustment
            </Button>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <History className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#101957]">{adjustments.length}</p>
                <p className="text-xs text-gray-500">Total Adjustments</p>
              </div>
            </div>
          </Card>
          <Card className={`p-4 ${pendingCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingCount > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                <Clock className={`w-5 h-5 ${pendingCount > 0 ? 'text-amber-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#101957]">{pendingCount}</p>
                <p className="text-xs text-gray-500">Pending Approval</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#101957]">
                  {adjustments.filter(a => a.status === 'APPROVED').length}
                </p>
                <p className="text-xs text-gray-500">Approved</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#101957]">
                  {adjustments.filter(a => a.status === 'REJECTED').length}
                </p>
                <p className="text-xs text-gray-500">Rejected</p>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Main Content */}
        <Card className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b px-4">
              <TabsList className="bg-transparent h-12">
                <TabsTrigger value="all" className="data-[state=active]:border-b-2 data-[state=active]:border-[#101957]">
                  All Adjustments
                </TabsTrigger>
                <TabsTrigger value="pending" className="data-[state=active]:border-b-2 data-[state=active]:border-[#101957]">
                  Pending Approval
                  {pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-2 text-xs">{pendingCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="approved" className="data-[state=active]:border-b-2 data-[state=active]:border-[#101957]">
                  Approved
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value={activeTab} className="p-0 m-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
                </div>
              ) : adjustments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Adjustment</TableHead>
                      <TableHead>Lot / Part</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map(adj => (
                      <AdjustmentRow
                        key={adj.adjustment_id}
                        adjustment={adj}
                        onView={(a) => {
                          setSelectedAdjustment(a);
                          // Could open a detail dialog here
                        }}
                        onApprove={(a) => {
                          setSelectedAdjustment(a);
                          setShowApproveDialog(true);
                        }}
                        onReject={(a) => {
                          setSelectedAdjustment(a);
                          setShowRejectDialog(true);
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <FileText className="w-12 h-12 mb-4 opacity-50" />
                  <p>No adjustments found</p>
                  <Button 
                    variant="link" 
                    onClick={() => setShowCreateDialog(true)}
                    className="mt-2"
                  >
                    Create your first adjustment
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
      
      {/* Create Adjustment Dialog */}
      <CreateAdjustmentDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={loadData}
        lots={lots}
        locations={[]}
        reasonCodes={reasonCodes}
      />
      
      {/* Approve Dialog */}
      <ESignatureDialog
        open={showApproveDialog}
        onOpenChange={setShowApproveDialog}
        onConfirm={handleApprove}
        title="Approve Adjustment"
        description="Approve this inventory adjustment with your electronic signature."
        actionDescription={selectedAdjustment ? 
          `${selectedAdjustment.adjustment_type} of ${selectedAdjustment.quantity} ${selectedAdjustment.uom} for lot ${selectedAdjustment.lot_number}` : ''
        }
        approvalLevel={selectedAdjustment?.approval_required}
        submitLabel="Sign & Approve"
      />
      
      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Reject Adjustment
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this adjustment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedAdjustment && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Adjustment:</strong> {selectedAdjustment.adjustment_number}</p>
                <p><strong>Lot:</strong> {selectedAdjustment.lot_number}</p>
                <p><strong>Quantity:</strong> {selectedAdjustment.quantity} {selectedAdjustment.uom}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Rejection Reason <span className="text-red-500">*</span></Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this adjustment is being rejected..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              <XCircle className="w-4 h-4 mr-2" />
              Reject Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </InventoryLayout>
  );
};

export default AdjustmentPage;
